import React from 'react';
import styled from 'styled-components';

import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';

type PaginationCircularProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const PaginationCircular = (componentProps: PaginationCircularProps) => {
  let props = componentProps;
  let isVisible = true;

  const dataElementContext = React.useContext(DataElementContext);
  [props, isVisible] = processComponentProps(props, dataElementContext);

  const defaultPerPage = 3;
  let perPage = parseInt(props.properties?.perPage ?? defaultPerPage);
  perPage = isNaN(perPage) ? defaultPerPage : perPage;

  const pinSelected = props.properties?.pinSelected === 'true' || props.properties?.pinSelected === true;
  const selectedValue = props.properties?.selectedValue;
  const selectedKey = props.properties?.selectedKey;

  const [state, setState] = React.useState({
    perPage: perPage,
    increment: 0,
    pinPosition: -1,
    list: props.properties.list?.slice(0, perPage) || [],
  });

  React.useEffect(() => {
    if (state.increment === 0 && props.properties.list?.length) {
      setState((prev) => {
        const newData = {
          ...prev,
          list: props.properties.list?.slice(0, prev.perPage) || [],
        };

        if (pinSelected && selectedKey && selectedValue && prev.pinPosition === -1) {
          const selectedElement = props.properties.list?.find((item: any) => item[selectedKey] === selectedValue);

          if (selectedElement) {
            const existingIndex = newData.list.findIndex((item: any) => item[selectedKey] === selectedValue);
            if (existingIndex !== -1) {
              //switch it on the first position
              const [element] = newData.list.splice(existingIndex, 1);
              newData.list.unshift(element);
              newData.pinPosition = 0;
            } else {
              newData.list[0] = selectedElement;
              newData.pinPosition = 0;
            }
          }
        }

        return newData;
      });
    }
  }, [props.properties.list, state.increment]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (pinSelected && !selectedValue) {
      setState((prev) => ({ ...prev, pinPosition: -1, increment: 0 }));
    }
  }, [pinSelected, selectedValue]);

  const onNext = React.useCallback(() => {
    setState((prev) => {
      const list: any[] = [];
      let perPage = prev.perPage;
      let length = props.properties.list?.length || 0;
      let elements = props.properties.list ?? [];
      let pinPosition = prev.pinPosition;

      if (pinSelected && selectedKey && selectedValue) {
        elements = elements.filter((item: any) => item[selectedKey] !== selectedValue);
        length = elements.length;
        perPage -= 1;

        if (pinPosition === -1) pinPosition = 0;
      }

      const increment = prev.increment + perPage;

      Array.from({ length: perPage }, (_, i) => {
        const index = increment + i;
        const n = length;
        const actualIndex = index % n;
        if (elements && elements[actualIndex]) {
          list.push(elements[actualIndex]);
        }
      });

      if (pinSelected && selectedKey && selectedValue) {
        const selectedElement = props.properties.list?.find((item: any) => item[selectedKey] === selectedValue);
        if (selectedElement && prev.pinPosition >= 0) {
          list.splice(prev.pinPosition, 0, selectedElement);
        }
      }

      return { ...prev, increment, list, pinPosition };
    });
  }, [pinSelected, selectedValue, selectedKey, props.properties.list]);

  const onPrevious = React.useCallback(() => {
    setState((prev) => {
      let perPage = prev.perPage;
      if (pinSelected && selectedKey && selectedValue) {
        perPage -= 1;
      }
      return { ...prev, increment: prev.increment - perPage };
    });
  }, [pinSelected, selectedValue, selectedKey]);

  const onPinPositionSelected = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    // @ts-ignore
    let pinPosition: any = e?.target?.dataset?.pinPosition ?? null;
    if (!pinPosition) return;

    pinPosition = parseInt(pinPosition);
    if (isNaN(pinPosition)) return;

    setState((prev) => {
      return { ...prev, pinPosition: pinPosition };
    });
  }, []);

  const contextValue = React.useMemo(() => {
    const data: any = {
      pinPosition: state.pinPosition,
      perPage: Math.min(state.perPage, props.properties.list?.length ?? 1000),
      increment: state.increment,
      list: state.list,
      parentContext: dataElementContext,
      actions: {
        onNext: onNext,
        onPrevious: onPrevious,
        onPinPositionSelected: onPinPositionSelected,
      },
    };

    return data;
  }, [
    state.pinPosition,
    state.list,
    state.perPage,
    state.increment,
    props.properties.list,
    onNext,
    onPrevious,
    dataElementContext,
    onPinPositionSelected,
  ]);

  if (!isVisible) return null;
  //console.log('Pagination[contextValue]', contextValue);

  return (
    <ModuleElementDiv className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default PaginationCircular;
